import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { XYChartModel } from '@ngx-seatback/charts';
import { DateTime, Duration } from 'luxon';
import { SbDateTime } from '../../../../services/api/api-date-time';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from '../../../../services/util.service';
import { API } from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ChartComponent } from '@ngx-seatback/charts';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'sb-comp-visitor-tracker-chart',
  templateUrl: './visitor-tracker-chart.component.html',
  styleUrls: ['./visitor-tracker-chart.component.scss'],
  standalone: false,
})
export class VisitorTrackerChartComponent implements OnInit {
  @ViewChild('chart') chart: ChartComponent;
  @ViewChild('chartLegend', { read: ElementRef, static: false }) chartLegendContainer: ElementRef;

  visitorTimeData: XYChartModel = { data: undefined, height: 0, loadingDone: false, title: '', valueAssignments: [] };
  visitorTimeDataTemp: any[];
  $destroy: Subject<any> = new Subject<any>();
  loading: boolean;
  timespan: Duration;
  interval: number = 86400;
  lastRequest: number;
  @Input() locationIds: number[];
  @Input() resolveInCom: boolean;
  @Input() halfHeight: boolean;
  @Input() classes: string;
  @Input() showChartControl: boolean = false;
  @Input() hideInterval: boolean = false;
  @Output() changedChartControl: EventEmitter<{ interval: number; dates: { fromDate: SbDateTime; toDate: SbDateTime } }>;

  constructor(
    private translate: TranslateService,
    public util: UtilService,
    private api: API,
    private errorHandler: ErrorService,
    private cdRef: ChangeDetectorRef
  ) {
    this.changedChartControl = new EventEmitter<{ interval: number; dates: { fromDate: SbDateTime; toDate: SbDateTime } }>();
    this.loading = true;
    this.visitorTimeData = {
      data: [],
      height: 0,
      loadingDone: false,
      title: '',
      valueAssignments: [],
    };
    this.timespan = Duration.fromMillis(0);
    this.interval = 86400;
    this.lastRequest = -1;
  }

  // tslint:disable-next-line:variable-name
  _chartHeight: number;

  get chartHeight() {
    return this._chartHeight;
  }

  @Input() set chartHeight(val) {
    if (val) {
      this._chartHeight = val;
    } else {
      this._chartHeight = 150;
    }
  }

  // tslint:disable-next-line:variable-name
  _from?: SbDateTime;

  get from(): SbDateTime {
    return this._from;
  }

  @Input() set from(val) {
    this._from = val;
  }

  // tslint:disable-next-line:variable-name
  _till?: SbDateTime;

  get till(): SbDateTime {
    return this._till;
  }

  @Input() set till(val) {
    this._till = val;
  }

  // tslint:disable-next-line:variable-name
  _inputData: any;

  get inputData() {
    return this._inputData;
  }

  @Input() set inputData(val) {
    this._inputData = val;
    if (!this.resolveInCom && this._inputData) {
      this.loading = true;
      this.cdRef.detectChanges();
      this.initChartData(this._inputData);
    }
  }

  ngOnInit(): void {
    if (this.resolveInCom) {
      this.refreshFromServer();
    }
    this.translate.onLangChange.pipe(takeUntil(this.$destroy)).subscribe(() => {
      this.buildChart();
    });
  }

  refreshFromServer() {
    this.loading = true;
    this.visitorTimeData.loadingDone = false;
    if (!this.from && !this.till) {
      this.from = new SbDateTime(DateTime.utc().startOf('day').minus({ day: 8 }));
      this.till = new SbDateTime(DateTime.utc().startOf('day').minus({ day: 1 }));
    } else {
      this.from = new SbDateTime(this.from.value.toUTC().startOf('day'));
      this.till = new SbDateTime(this.till.value.toUTC().startOf('day'));
    }
    this.api
      .dm_visitor_VisitorTrackerReporting_trackedVisitorFramed({
        locations: this.locationIds,
        from: this.from,
        till: this.till,
        productive: true,
        interval: this.interval,
      })
      .subscribe(
        data => {
          this.initChartData(data.buckets[0].frames);
        },
        error => {
          this.errorHandler.errorHandler(error);
        }
      );
  }

  initChartData(data) {
    if (this.from && this.till) {
      this.timespan = DateTime.fromJSDate(this.util.localDateToJSDate(this.till)).diff(
        DateTime.fromJSDate(this.util.localDateToJSDate(this.from)),
        'days'
      );
    }
    this.visitorTimeDataTemp = [];
    for (const i of data) {
      this.visitorTimeDataTemp.push({
        date: i.range.from,
        count: i.data.count,
        unique: i.data.unique,
      });
    }
    this.buildChart();
  }

  buildChart() {
    this.translate.get(marker('lang.globals.visitor')).subscribe(visitor => {
      this.translate.get(marker('lang.globals.impressions')).subscribe(impressions => {
        this.visitorTimeData = {
          title: 'Test',
          height: this.chartHeight,
          valueAssignments: [
            {
              value: 'count',
              valueName: visitor,
              color: '#147da8',
              categories: ['date'],
            },
            /*{ // TODO implement impressions
              value: 'unique',
              valueName: impressions,
              color: '#2cc361',
              categories: ['date'],
            }*/
          ],
          categoryType: 'date',
          legend: true,
          legendContainer: this.chartLegendContainer,
          legendAlign: 'right',
          legendContainerWidth: '600px',
          loadingDone: true,
          tensionX: 1,
          data: this.visitorTimeDataTemp,
        };
        this.loading = false;
        this.cdRef.detectChanges();
        this.chart.rerenderChart(this.visitorTimeData);
      });
    });
  }

  setInterval(interval) {
    if (interval) {
      this.interval = interval;
      this.emitChange();
    }
  }

  setTimeFrame(dates?: { fromDate: SbDateTime; toDate: SbDateTime }) {
    if (dates) {
      this.from = dates.fromDate;
      this.till = dates.toDate;
      this.emitChange();
    }
  }

  emitChange() {
    this.changedChartControl.emit({
      interval: this.interval,
      dates: {
        fromDate: this.from,
        toDate: this.till,
      },
    });
  }
}
